import { ADMIN_COLORS } from '../constants/bookingConst';
import { ERROR_LOG_SOURCE_LABELS } from '../utils/errorLogUtil';
import '../css/AdminErrorLogs.css';

const COLORS = ADMIN_COLORS;

/** "2:32 PM" — the day is already the row's heading. */
const formatTime = (ts) => (ts
  ? new Date(ts).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  : '—');

/** "Sep 21, 2026" from a "YYYY-MM-DD" day key. */
const formatDayLabel = (dayKey) => {
  const [y, m, d] = (dayKey || '').split('-').map(Number);
  if (!y || !m || !d) return dayKey || 'Unknown day';
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

/**
 * One day of errors, presented as the .txt file it would download as.
 *
 * The file name is shown rather than invented at download time so that what
 * an admin reads on screen and what lands in their Downloads folder are
 * visibly the same thing — the file does not exist until they press the
 * button (it is rendered from the records), and this row is what stands in
 * for it until then.
 *
 * @param {object} props
 * @param {{dayKey: string, entries: Array<object>, errorCount: number,
 *   warnCount: number, lastTimestamp: number}} props.group
 * @param {boolean} props.expanded
 * @param {Function} props.onToggle - (dayKey) => void
 * @param {Function} props.onDownload - (group) => void
 * @param {Function} props.onDelete - (group) => void
 * @param {Function} props.onSelectEntry - (entry) => void
 * @returns {JSX.Element}
 */
function ErrorLogFileRow({ group, expanded, onToggle, onDownload, onDelete, onSelectEntry }) {
  const { dayKey, entries, errorCount, warnCount, lastTimestamp } = group;

  return (
    <div className={`ael-file ${expanded ? 'is-expanded' : ''}`}>
      <div className="ael-file-head">
        <button
          type="button"
          className="ael-file-toggle"
          onClick={() => onToggle(dayKey)}
          aria-expanded={expanded}
        >
          <span className="ael-file-caret">{expanded ? '▾' : '▸'}</span>
          <span className="ael-file-names">
            <span className="ael-file-name">errors-{dayKey}.txt</span>
            <span className="ael-file-meta">
              {formatDayLabel(dayKey)} · {entries.length} entr{entries.length === 1 ? 'y' : 'ies'} · last at {formatTime(lastTimestamp)}
            </span>
          </span>
        </button>

        <div className="ael-file-counts">
          {errorCount > 0 && (
            <span className="ael-count" style={{ color: COLORS.danger, background: `${COLORS.danger}1a` }}>
              {errorCount} error{errorCount === 1 ? '' : 's'}
            </span>
          )}
          {warnCount > 0 && (
            <span className="ael-count" style={{ color: COLORS.warning, background: `${COLORS.warning}1a` }}>
              {warnCount} warning{warnCount === 1 ? '' : 's'}
            </span>
          )}
        </div>

        <div className="ael-file-actions">
          <button type="button" className="ael-btn ael-btn-primary" onClick={() => onDownload(group)}>
            Download
          </button>
          <button type="button" className="ael-btn ael-btn-danger" onClick={() => onDelete(group)}>
            Delete
          </button>
        </div>
      </div>

      {expanded && (
        <div className="ael-file-body">
          <div className="ael-table-wrapper">
            <table className="ael-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Level</th>
                  <th>Source</th>
                  <th>Code</th>
                  <th>Message</th>
                  <th>Screen</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => {
                  const isWarn = entry.level === 'warn';
                  return (
                    <tr
                      key={entry.id}
                      className="ael-row"
                      onClick={() => onSelectEntry(entry)}
                      title="Open the full record"
                    >
                      <td className="ael-cell-time">{formatTime(entry.timestamp)}</td>
                      <td>
                        <span
                          className="ael-level-pill"
                          style={{
                            color: isWarn ? COLORS.warning : COLORS.danger,
                            background: `${isWarn ? COLORS.warning : COLORS.danger}1a`,
                          }}
                        >
                          {isWarn ? 'Warn' : 'Error'}
                        </span>
                      </td>
                      <td className="ael-cell-source">{ERROR_LOG_SOURCE_LABELS[entry.source] || entry.source}</td>
                      <td className="ael-cell-code">{entry.code || '—'}</td>
                      <td className="ael-cell-message">
                        {entry.message}
                        {/* Surfaced in the table, not just the detail sheet:
                            a repeat count is the difference between a one-off
                            and a loop, and that is the first thing worth
                            knowing when scanning a bad day. */}
                        {entry.repeatCount ? (
                          <span className="ael-repeat-badge">+{entry.repeatCount}</span>
                        ) : null}
                      </td>
                      <td className="ael-cell-screen">{entry.screen || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default ErrorLogFileRow;
